import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import {AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild} from '@angular/core';
import {AmchartsService} from '../../shared/_services/amcharts.service';
import {IM_DF_CUM_SUFFIX, IM_DF_OC_SUFFIX, IM_DF_PNL_SUFFIX} from '../_models/plant-risk.model';
import {Subject} from 'rxjs';

@Component({
  selector: 'app-outage-chart',
  templateUrl: './outage-chart.component.html',
  styleUrls: ['./outage-chart.component.scss']
})

export class OutageChartComponent implements AfterViewInit, OnDestroy {

  @Input() asset: string;
  @Input() country: string;
  @Input() ocData: any[];

  @ViewChild('outage_chartdiv') chartDiv: ElementRef;

  private chart: am4charts.XYChart;

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private amChartService: AmchartsService,
    private ngZone: NgZone,
  ) {
  }

  ngAfterViewInit() {
    this.updateChartData();
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
    this.onDestroy$.next();
  }

  // ********************************************************************************************************
  // LOAD DATA
  // ********************************************************************************************************

  // ********************************************************************************************************
  // PROCESS DATA
  // ********************************************************************************************************

  private updateChartData(): void {
    this.createChart();
    if (this.chart && this.ocData) {
      this.ngZone.runOutsideAngular(() => {
        this.chart.data = this.ocData;
      });
    }
  }

  // ********************************************************************************************************
  // UI
  // ********************************************************************************************************

  private createChart(): void {
    if (!this.chart && this.chartDiv?.nativeElement) {
      this.ngZone.runOutsideAngular(() => {
        const chart = am4core.create('outage__chart_' + this.country + this.asset, am4charts.XYChart);
        this.amChartService.setChartDefaults(chart, true, this.asset);
        chart.dateFormatter.inputDateFormat = 'yyyy-MM-dd HH:mm';
        chart.paddingRight = 24;

        // AXES
        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        this.amChartService.setDateAxis(dateAxis, 'date', true, false, true, 80);
        dateAxis.tooltipDateFormat = 'yyyy-MM-dd HH:mm';
        dateAxis.dateFormats.setKey('day', 'MMM dd \'(\'EEE\')\'');
        const valueAxis1 = chart.yAxes.push(new am4charts.ValueAxis());
        this.amChartService.setValueAxis(valueAxis1, 'EUR');
        const valueAxis2 = chart.yAxes.push(new am4charts.ValueAxis());
        this.amChartService.setValueAxis(valueAxis2, '', false, false);
        valueAxis2.renderer.opposite = true;

        // SERIES
        const seriesOc = chart.series.push(new am4charts.ColumnSeries());
        this.amChartService.setSeries(
          seriesOc,
          'date',
          'outage cost',
          'ts',
          this.asset + IM_DF_OC_SUFFIX,
          null,
          null,
        );
        seriesOc.yAxis = valueAxis1;
        seriesOc.xAxis = dateAxis;
        seriesOc.fillOpacity = 1;
        seriesOc.strokeWidth = 0;

        const seriesOcCum = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesOcCum,
          'date',
          'cumulative outage cost',
          'ts',
          this.asset + IM_DF_OC_SUFFIX + IM_DF_CUM_SUFFIX,
          null,
          this.amChartService.RED,
        );
        seriesOcCum.yAxis = valueAxis2;
        seriesOcCum.xAxis = dateAxis;
        seriesOcCum.connect = true;

        const seriesPnlCum = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesPnlCum,
          'date',
          'cumulative p&l',
          'ts',
          this.asset + IM_DF_PNL_SUFFIX + IM_DF_CUM_SUFFIX,
          null,
          this.amChartService.GREEN,
        );
        seriesPnlCum.yAxis = valueAxis2;
        seriesPnlCum.xAxis = dateAxis;
        seriesPnlCum.connect = true;

        this.chart = chart;

      });
    }
  }

}
